import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { getMeetingsByUser, getMeetingInvitations, sendInvitation, subscribeToMeetingInvitations, subscribeToMeetingStatus } from '../../services/meetingService';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import InvitationCard from './components/InvitationCard';
import InvitationStats from './components/InvitationStats';
import BulkActions from './components/BulkActions';
import { trackButtonClick, trackEngagementTime } from '../../utils/analytics';

const InvitationStatus = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedInvitations, setSelectedInvitations] = useState([]);
  const [resendingIds, setResendingIds] = useState([]);
  const [pageLoadTime, setPageLoadTime] = useState(null);

  useEffect(() => {
    setPageLoadTime(Date.now());
    
    return () => {
      if (pageLoadTime) {
        const timeSpent = Math.floor((Date.now() - pageLoadTime) / 1000);
        trackEngagementTime('Invitation Status', timeSpent);
      }
    };
  }, []);

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      navigate('/signin');
      return;
    }

    loadData();
  }, [user, authLoading, navigate]);

  // Real-time subscription for invitation and meeting status changes
  useEffect(() => {
    if (!meetings || meetings?.length === 0) return;

    const unsubscribeFunctions = [];

    // Subscribe to invitation changes for each meeting
    meetings?.forEach(meeting => {
      // Subscribe to invitation status changes
      const unsubInvitations = subscribeToMeetingInvitations(meeting?.id, (payload) => {
        console.log('Invitation change detected:', payload);
        // Reload data when invitation status changes
        loadData();
      });
      unsubscribeFunctions?.push(unsubInvitations);

      // Subscribe to meeting status changes
      const unsubMeetingStatus = subscribeToMeetingStatus(meeting?.id, (payload) => {
        console.log('Meeting status change detected:', payload);
        // Reload data when meeting status changes
        loadData();
      });
      unsubscribeFunctions?.push(unsubMeetingStatus);
    });

    // Cleanup all subscriptions on unmount or when meetings change
    return () => {
      unsubscribeFunctions?.forEach(unsub => unsub?.());
    };
  }, [meetings]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const meetingsData = await getMeetingsByUser();
      setMeetings(meetingsData || []);

      const allInvitations = [];
      for (const meeting of meetingsData || []) {
        const meetingInvitations = await getMeetingInvitations(meeting?.id);
        const enrichedInvitations = meetingInvitations?.map(inv => ({
          ...inv,
          meetingDetails: meeting
        }));
        allInvitations?.push(...enrichedInvitations);
      }
      
      setInvitations(allInvitations);
    } catch (err) {
      console.error('Load data error:', err);
      setError('Failed to load invitation data');
    } finally {
      setLoading(false);
    }
  };

  const categorizeInvitations = () => {
    const accepted = invitations?.filter(inv => inv?.status === 'accepted') || [];
    const pending = invitations?.filter(inv => inv?.status === 'pending') || [];
    const declined = invitations?.filter(inv => inv?.status === 'declined') || [];

    return { accepted, pending, declined };
  };

  const { accepted, pending, declined } = categorizeInvitations();

  const getFilteredInvitations = () => {
    switch (activeTab) {
      case 'accepted':
        return accepted;
      case 'pending':
        return pending;
      case 'declined':
        return declined;
      default:
        return [];
    }
  };

  const filteredInvitations = getFilteredInvitations();

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSelectedInvitations([]);
    trackButtonClick(`invitation_tab_${tab}`, `${tab} Tab`);
  };

  const handleSelectInvitation = (invitationId) => {
    setSelectedInvitations(prev => {
      if (prev?.includes(invitationId)) {
        return prev?.filter(id => id !== invitationId);
      }
      return [...prev, invitationId];
    });
  };

  const handleSelectAll = () => {
    if (selectedInvitations?.length === filteredInvitations?.length) {
      setSelectedInvitations([]);
    } else {
      setSelectedInvitations(filteredInvitations?.map(inv => inv?.id));
    }
  };

  const handleResendInvitation = async (invitationId) => {
    try {
      setResendingIds(prev => [...prev, invitationId]);
      await sendInvitation(invitationId, true, false);
      trackButtonClick('resend_invitation', 'Resend Invitation');
      
      // Show success feedback
      const invitation = invitations?.find(inv => inv?.id === invitationId);
      if (invitation) {
        setError('');
      }
    } catch (err) {
      console.error('Resend invitation error:', err);
      setError('Failed to resend invitation');
    } finally {
      setResendingIds(prev => prev?.filter(id => id !== invitationId));
    }
  };

  const handleBulkResend = async () => {
    try {
      setError('');
      const resendPromises = selectedInvitations?.map(id => 
        sendInvitation(id, true, false)
      );
      await Promise.all(resendPromises);
      trackButtonClick('bulk_resend_invitations', 'Bulk Resend Invitations');
      setSelectedInvitations([]);
    } catch (err) {
      console.error('Bulk resend error:', err);
      setError('Failed to resend some invitations');
    }
  };

  const getTimeSinceInvitation = (sentAt) => {
    if (!sentAt) return 'Unknown';
    const now = new Date();
    const sent = new Date(sentAt);
    const diffMs = now - sent;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffMins > 0) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    return 'Just now';
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading invitations...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Invitation Status - Mosey</title>
        <meta name="description" content="Track participant responses and manage meeting invitations" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg">
                <Icon name="Users" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="font-heading text-3xl font-bold text-foreground">
                  Invitation Status
                </h1>
                <p className="text-muted-foreground">
                  Track participant responses and manage invitations
                </p>
              </div>
            </div>
          </div>

          {/* Statistics Overview */}
          <InvitationStats 
            acceptedCount={accepted?.length || 0}
            pendingCount={pending?.length || 0}
            declinedCount={declined?.length || 0}
            totalInvitations={invitations?.length || 0}
          />

          {/* Main Content */}
          <div className="bg-card rounded-lg shadow-md border border-border p-6">
            {/* Tabs and Bulk Actions */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant={activeTab === 'pending' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleTabChange('pending')}
                >
                  Pending ({pending?.length || 0})
                </Button>
                <Button
                  variant={activeTab === 'accepted' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleTabChange('accepted')}
                >
                  Accepted ({accepted?.length || 0})
                </Button>
                <Button
                  variant={activeTab === 'declined' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleTabChange('declined')}
                >
                  Declined ({declined?.length || 0})
                </Button>
              </div>

              {(activeTab === 'pending' || activeTab === 'declined') && filteredInvitations?.length > 0 && (
                <BulkActions
                  selectedCount={selectedInvitations?.length}
                  totalCount={filteredInvitations?.length}
                  onSelectAll={handleSelectAll}
                  onBulkResend={handleBulkResend}
                  onClearSelection={() => setSelectedInvitations([])}
                />
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-4 bg-error/10 border border-error/20 rounded-lg flex items-start gap-3">
                <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-error">{error}</p>
                </div>
              </div>
            )}

            {/* Invitations List */}
            {filteredInvitations?.length === 0 ? (
              <div className="text-center py-12">
                <Icon name="Mail" size={48} className="text-muted-foreground mx-auto mb-4" />
                <h3 className="font-heading text-lg font-semibold text-foreground mb-2">
                  No {activeTab} invitations
                </h3>
                <p className="text-muted-foreground mb-6">
                  {activeTab === 'pending' && 'All participants have responded to their invitations'}
                  {activeTab === 'accepted' && 'No participants have accepted yet'}
                  {activeTab === 'declined' && 'No participants have declined'}
                </p>
                <Button
                  variant="outline"
                  iconName="ArrowLeft"
                  iconPosition="left"
                  onClick={() => navigate('/home-dashboard')}
                >
                  Back to Dashboard
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredInvitations?.map((invitation) => (
                  <InvitationCard
                    key={invitation?.id}
                    invitation={invitation}
                    status={activeTab}
                    isSelected={selectedInvitations?.includes(invitation?.id)}
                    onSelect={() => handleSelectInvitation(invitation?.id)}
                    onResend={() => handleResendInvitation(invitation?.id)}
                    isResending={resendingIds?.includes(invitation?.id)}
                    timeSince={getTimeSinceInvitation(invitation?.sentAt)}
                    showCheckbox={activeTab === 'pending' || activeTab === 'declined'}
                  />
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default InvitationStatus;